/**
 * Application entry point: build the context, wire the controllers, mount the UI, restore the
 * last project, and expose the headless test surface (plan D24, see `./testSurface.ts`).
 *
 * Order matters and is part of the wave-3 contract:
 *  1. `createControllers(ctx)` — it assigns `ctx.rigTools`, which the viewport reads at mount;
 *  2. `installErrorHandlers` / `installDatabaseBlockedHandler` — see below; they must be live
 *     BEFORE anything that can throw;
 *  3. `mountShell` — nothing else has containers to mount into until it has run;
 *  4. `mountViewport` / `mountTimeline` into the shell's containers.
 */

import './style.css';

import {
  createPendingSaveTracker,
  createSession,
  createStore,
  installDraftAutosave,
  installPersistenceLifecycle,
  restoreLastSession,
  LAST_PROJECT_KEY,
} from './editor';
import { createEventBus } from './editor/context';
import type { EditorContext, RigTools, ViewportHandle } from './editor/context';
import { createControllers } from './editor/controllers';
import { mountShell } from './editor/app';
import { mountViewport } from './editor/viewport';
import { mountTimeline } from './editor/timeline';
import type { TimelineHandle } from './editor/timeline';
import { onDatabaseBlocked, settingsRepo } from './persistence';
import { isAppError } from './shared';
import { installTestSurface } from './testSurface';

export const APP_VERSION = '0.1.0-mvp';

/** Re-exported so the settings key keeps one public home (it lives in `editor/sessionRestore`). */
export { LAST_PROJECT_KEY };

function bootstrap(): void {
  const root = document.getElementById('app');
  if (!root) throw new Error('#app is missing from index.html');

  const ctx: EditorContext = {
    store: createStore(),
    session: createSession(),
    events: createEventBus(),
    // `createControllers` fills this in immediately below; nothing reads it before then.
    rigTools: null as unknown as RigTools,
  };

  // One flag, two writers: the project save and the clip draft each report into the tracker,
  // which ORs them into `ui.saving` for the toolbar.
  const pending = createPendingSaveTracker(ctx);

  const controllers = createControllers(ctx, { onPendingChange: pending.track('project') });

  /*
   * These two used to be installed AFTER the three mounts (audit §2.6 #2). That ordering was a
   * silent trap: `mountViewport` constructs the `WebGLRenderer`, which throws on a browser with
   * hardware acceleration switched off, and the throw escaped past a `window.onerror` handler
   * that did not exist yet. The result was the worst possible failure — the shell painted, the
   * viewport and timeline never mounted, and not one word of explanation on screen. Neither
   * installer touches the DOM or the mounted panels, so nothing stops them going first.
   */
  installErrorHandlers(ctx);
  installDatabaseBlockedHandler(ctx);

  // `settingsRepo` is the column-width store: the layout the user drags survives a reload.
  const shell = mountShell(root, ctx, controllers, { layoutStore: settingsRepo });

  /*
   * The mounts are the only part of bootstrap that depends on the browser's 3D capabilities, so
   * they are the part that gets a fallback. A failure here is terminal for the editor — there is
   * no usable editor without a viewport — so we tear down what was already built and replace the
   * page with a readable message rather than leaving a half-drawn shell behind.
   */
  let mounted: { viewport: ViewportHandle; timeline: TimelineHandle } | null = null;
  try {
    const viewportHandle = mountViewport(shell.containers.viewport, ctx);
    try {
      mounted = { viewport: viewportHandle, timeline: mountTimeline(shell.containers.bottom, ctx) };
    } catch (error) {
      // The viewport mounted and the timeline did not: it owns a live WebGL context and a
      // ResizeObserver, so it has to be released before the page is replaced.
      viewportHandle.dispose();
      throw error;
    }
  } catch (error) {
    shell.dispose();
    controllers.dispose();
    renderStartupFailure(root, error);
    return;
  }
  const { viewport, timeline } = mounted;

  rememberLastProject(ctx);

  const drafts = installDraftAutosave(ctx.store, {
    projectId: () => controllers.project.currentProjectId(),
    onPendingChange: pending.track('draft'),
  });

  const lifecycle = installPersistenceLifecycle(ctx, {
    sinks: [
      { flush: () => controllers.flushPendingSaves(), isPending: () => controllers.hasPendingSaves() },
      drafts,
      /*
       * The layout's write is 300 ms behind the drag that caused it, so a reload inside that
       * window used to lose the size the user had just set. It is a real pending write and the
       * unload path has to flush it — but it is deliberately NOT wired into the pending-save
       * tracker: dragging a splitter is not the user's work, and flashing "Saving…" for it would
       * devalue the one indicator that answers "is my animation on disk?".
       */
      { flush: () => shell.layout.flush(), isPending: () => shell.layout.pending() },
    ],
  });

  // Development-only driving surface: it exposes the store, the session and every controller, so
  // it has no business in a production bundle (audit P2). `import.meta.env.DEV` is true under
  // `vite dev` — which is what the headless harness and the playtests run against — and false in
  // `vite build`, where the whole block is dropped by dead-code elimination.
  if (import.meta.env.DEV) {
    installTestSurface({
      ctx,
      controllers,
      viewport,
      // So a headless run can drag a splitter, `await layout.flush()` and reload to check that
      // the size was persisted.
      layout: shell.layout,
      version: APP_VERSION,
      // `whenIdle()` must wait for the clip draft too, not just the project save scheduler.
      flushAll: () => lifecycle.flushNow(),
      dispose: () => {
        lifecycle.dispose();
        timeline.dispose();
        viewport.dispose();
        shell.dispose();
        drafts.dispose();
        controllers.dispose();
      },
    });
  }

  void controllers.library.refresh();
  void controllers.mapping.refreshPresets();
  void restoreLastSession(ctx.store, controllers.project, settingsRepo);
}

/**
 * Replace the page with a plain, readable explanation when the editor cannot start at all.
 *
 * This is the last line of defence for audit §2.6 #2. `installErrorHandlers` cannot help here:
 * its report path runs through the store and the toolbar, and if the mounts failed there may be
 * no toolbar to render into. So this writes DOM directly, uses no CSS class the shell owns, and
 * builds every node with `textContent` — the message can contain an exception string, which must
 * never be interpreted as markup.
 *
 * An `AppError` already carries a message written for a human (`VIEWPORT_NO_WEBGL` explains
 * hardware acceleration), so it is shown verbatim; anything else gets the generic line plus the
 * raw text, which is what a bug report needs.
 *
 * DELIBERATELY UNSTYLED. This shipped for a while as a themed dark panel (a set colour, a set
 * background, a set font), which is a visual direction — and visual directions in this project are
 * shown and approved before any styling effort is spent, not decided inside a bug fix. The one
 * declaration left is `white-space:pre-wrap`, which is not decoration: the detail text carries the
 * newlines that separate the explanation from the raw exception, and without it they collapse.
 * Everything else renders in the browser's defaults, which are readable on any page whose own
 * stylesheet has just failed to matter.
 */
function renderStartupFailure(root: HTMLElement | null, error: unknown): void {
  const detail = error instanceof Error ? error.message : String(error);
  console.error('[intch-motion-kit] startup failed', error);
  if (!root) return;

  const panel = document.createElement('div');

  const heading = document.createElement('h1');
  heading.textContent = "INTCH's Motion Kit could not start";

  const body = document.createElement('p');
  body.style.whiteSpace = 'pre-wrap';
  body.textContent = isAppError(error)
    ? detail
    : `The editor hit an unexpected error while starting up.\n\n${detail}`;

  const hint = document.createElement('p');
  hint.textContent =
    'Your saved projects are untouched — they live in this browser’s local database and are ' +
    'still there after a reload.';

  panel.append(heading, body, hint);
  root.classList.remove('shell');
  root.replaceChildren(panel);
}

/**
 * Uncaught errors are both a developer signal and a user-visible failure: log the detail, surface
 * a short line in the UI, and never let one become an invisible dead end.
 */
function installErrorHandlers(ctx: EditorContext): void {
  const report = (label: string, detail: unknown): void => {
    const message = detail instanceof Error ? detail.message : String(detail);
    console.error(`[intch-motion-kit] ${label}`, detail);
    ctx.store.batch(() => {
      ctx.store.actions.pushError(`${label}: ${message}`);
      ctx.store.actions.toast('error', message);
    });
  };

  window.addEventListener('error', (event) => {
    report('Unexpected error', event.error ?? event.message);
  });
  window.addEventListener('unhandledrejection', (event) => {
    report('Unexpected error', event.reason);
  });
}

/** Remember which project is open so the next load can reopen it (SPEC Scenario D). */
function rememberLastProject(ctx: EditorContext): void {
  ctx.store.select(
    (state) => state.project?.id ?? null,
    (id) => {
      // Store listeners are synchronous; these repo calls are async and dispatch nothing back.
      if (id) void settingsRepo.set(LAST_PROJECT_KEY, id);
      else void settingsRepo.remove(LAST_PROJECT_KEY);
    },
  );
}

/**
 * IndexedDB refuses to upgrade or delete while another tab holds the old connection. Logging that
 * to the console leaves the user staring at an app that simply does not finish (audit P1), so it
 * becomes visible state the toolbar renders.
 */
function installDatabaseBlockedHandler(ctx: EditorContext): void {
  onDatabaseBlocked((blocked) => {
    ctx.store.batch(() => {
      ctx.store.actions.setDbBlocked(blocked);
      if (blocked) {
        ctx.store.actions.toast(
          'error',
          'Motion Kit is open in another tab and is holding the local database. Close the other ' +
            'tabs, then reload this one.',
        );
      }
    });
  });
}

/*
 * Even with the handlers moved up, `bootstrap` itself can throw before they are installed (a
 * missing `#app`, a controller constructor). Nothing above this line can catch that, and an
 * uncaught throw here is exactly the blank page §2.6 #2 is about — so the entry call gets the
 * same fallback as the mounts.
 */
try {
  bootstrap();
} catch (error) {
  renderStartupFailure(document.getElementById('app'), error);
}
